import { useEffect } from 'react';
import { connect } from 'react-redux';
import { AppState, ConnectedPropsWithoutDispatch } from '../../../../stores/redux-store';
import { HeaderProps } from '../../../organisms/Header/Header';
import { applicationParameters } from '../../../../utilities/application-parameters';
import { Provider, ProviderEntity } from '../../../../models/provider';
import { useAnalytics } from '../../../../core/Analytics/AnalyticsProvider';
import { PageView } from '../../../../models/analytics';
import { SubmitCredentialsPayload } from '../../SubmitCredentials/hooks/useSubmitCredentials/useSubmitCredentials';
import { useConfiguration } from '../../../ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { useShouldShowUserHintId } from '../../SubmitCredentials/hooks/useShouldShowUserHintId';
import { userFeedbackEnabled } from '../../../../models/configuration';
import { parentMessenger } from '../../../../core/Analytics/ParentApplicationEventMessenger';
import { BackButton } from '../../../organisms/Header/HeaderMolecule';
import { SubmitCredentialsScreenView } from './screens/LegacySubmitCredentialsScreenView/LegacySubmitCredentialsScreenView';

export type SubmitCredentialsScreenOwnProps = {
  bodyBlock?: JSX.Element;
  providerFieldsFallback?: JSX.Element;
  userHintId?: string;
  buttonLabel?: JSX.Element;
  provider: ProviderEntity;
  renderProviderFields: boolean;
  onSubmitCredentials: (payload: SubmitCredentialsPayload) => void;
  legalDisclaimer?: JSX.Element;
  Header?: React.ComponentType<HeaderProps>;
} & BackButton;

export type SubmitCredentialsScreenProps = PropsFromRedux & SubmitCredentialsScreenOwnProps;

export const SubmitCredentialsScreenPresenter: React.FC<SubmitCredentialsScreenProps> = ({
  userHintId,
  provider,
  credentialsError,
  isClientVerified,
  renderProviderFields,
  onSubmitCredentials,
  onBack,
  buttonLabel,
  bodyBlock,
  providerFieldsFallback,
  session,
  legalDisclaimer,
  isCredentialsLoading,
}) => {
  const { tinklinkAnalytics } = useAnalytics();
  const { data: configurationData } = useConfiguration();
  const { shouldShowUserHintId } = useShouldShowUserHintId();

  useEffect(() => {
    tinklinkAnalytics.sendViewEvent({
      view: PageView.LEGACY_SUBMIT_CREDENTIALS_SCREEN,
    });
    parentMessenger.fireViewEvent(PageView.SUBMIT_CREDENTIALS_SCREEN);
  }, [tinklinkAnalytics]);

  useEffect(() => {
    if (credentialsError) {
      parentMessenger.fireErrorEvent({
        pageView: PageView.SUBMIT_CREDENTIALS_SCREEN,
        errorMessage: credentialsError,
      });
    }
  }, [credentialsError]);

  const username = applicationParameters.get('username');
  const personalIdentifier = session?.personalIdentifier;
  const providerFieldsVisible = renderProviderFields && Provider.hasFields(provider);

  return (
    <SubmitCredentialsScreenView
      bodyBlock={bodyBlock}
      providerFieldsFallback={providerFieldsFallback}
      userHintId={userHintId}
      buttonLabel={buttonLabel}
      provider={provider}
      renderProviderFields={renderProviderFields}
      onBack={onBack}
      onSubmitCredentials={(fields) => {
        onSubmitCredentials({ fields });
      }}
      legalDisclaimer={legalDisclaimer}
      isCredentialsLoading={isCredentialsLoading}
      username={username}
      personalIdentifier={personalIdentifier}
      providerFieldsVisible={providerFieldsVisible}
      shouldShowUserHintId={shouldShowUserHintId}
      isClientVerified={isClientVerified}
      credentialsError={credentialsError}
      isUserFeedbackEnabled={userFeedbackEnabled(configurationData)}
    />
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    credentialsError: state.credentials.error,
    session: state.session.data,
    isClientVerified: Boolean(state.oauth.description?.verified),
    isCredentialsLoading: state.credentials.loading,
  };
};

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedPropsWithoutDispatch<typeof connector>;

export const LegacySubmitCredentialsScreen = connector(SubmitCredentialsScreenPresenter);
